/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import axios from 'axios';
import op from 'object-path';
import * as JSZip from 'jszip';
import MD5 from 'js-md5';
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import ENVS from '@/modules/envs';
import Storage from '@/modules/storage';
import $api from './request';
import { wait } from './utils';

const SCANNERID_STORAGE_KEY = 'tg3d-scanner';
const TID_STORAGE_KEY = 'tg3d-tid';
const LAST_SCANNERID_STORAGE_KEY = 'tg3d-last-scanner';
const LAST_TID_STORAGE_KEY = 'tg3d-last-tid';

const SCAN_VER = 2;
const PROTOCOL_VER = '4';

const DEFAULT_USER_HEIGHT = 175;

const ScanProcess = {
  scannerId: localStorage ? localStorage.getItem(SCANNERID_STORAGE_KEY) || '' : '',
  key: '',
  qrToken: '',
  tid: localStorage ? localStorage.getItem(TID_STORAGE_KEY) || '' : '',
  scanVer: SCAN_VER,
  protocolVer: PROTOCOL_VER,
  type: '',
  in3dId: '',
  mobileScanHeight: DEFAULT_USER_HEIGHT,
  mobileScanResult: null,
  chargeOnUser: false,
  latestUpdateArmTime: 0,

  resetState() {
    this.scannerId = '';
    this.key = '';
    this.qrToken = '';
    this.tid = '';
    this.type = '';
    this.in3dId = '';
    this.mobileScanHeight = DEFAULT_USER_HEIGHT;
    this.mobileScanResult = null;
    this.scanVer = SCAN_VER;
    this.protocolVer = PROTOCOL_VER;
    this.chargeOnUser = false;
    this.mobileScanPrice = '';
    this.latestUpdateArmTime = 0;
    if (localStorage) {
      localStorage.removeItem(SCANNERID_STORAGE_KEY);
      localStorage.removeItem(TID_STORAGE_KEY);
    }
  },

  getSessionKey(scannerId) {
    if (ENVS.mock) return wait(3).then(() => 'FakeKey');

    const { TG3DCloudzetEvents: $events } = window;
    const targetId = scannerId || this.scannerId;
    $events.emit(HW_EVENTS.START_BT_SCAN, true);
    return new Promise((resolve, reject) => {
      let timer = null;
      let count = 0;
      $events.once(HW_EVENTS.ON_SCANNER_KEY, key => {
        console.log(`got key for scanner ${targetId}: ${key}`);
        clearTimeout(timer);
        resolve(key);
      });
      const getKey = () => {
        console.log(`getting session key for ${targetId}...`);
        $events.emit(HW_EVENTS.GET_SCANNER_KEY, targetId);
        count += 1;
        if (count < 5) {
          timer = setTimeout(getKey, 1000);
        } else {
          reject(new Error('Failed to get session key'));
        }
      };
      wait(3).then(() => getKey());
    });
  },

  async initial(
    {
      id,
      key,
      qrToken = '',
      scanVer = SCAN_VER,
      snapshots = false,
      type = 'body',
      provider = 0,
      token = '',
    },
    chargeOnUser = false,
    mobileScanPrice = ''
  ) {
    this.provider = provider;
    this.key = key;
    this.qrToken = qrToken;
    this.token = token;
    this.type = type;
    this.chargeOnUser = chargeOnUser;
    this.mobileScanPrice = mobileScanPrice;
    const data = {
      protocol_ver: PROTOCOL_VER,
      scan_ver: scanVer,
      snapshots,
      charge_on_user: chargeOnUser,
      mobile_scan_price: mobileScanPrice,
      // a store QR from `dc=2` brings its own one time token, which replaces the
      // session key. Never send both: the backend judges the token alone and a
      // spent one is a hard 40308, it does not fall back to the key.
      ...(qrToken ? { qr_token: qrToken } : { session_key: key }),
    };
    let resp = { data };
    if (localStorage) {
      localStorage.setItem(LAST_SCANNERID_STORAGE_KEY, id);
    }
    if (ENVS.mock) {
      resp = { data: { tid: 'mock-tid' } };
      await wait(4);
    } else {
      this.reportEvent('initial');
      resp = await $api.user.post(`/api/v1/scanners/${id}/`, data);
    }
    const {
      tid,
      scan_ver: scanVerResp,
      protocol_ver: protocolVer,
      in3d_id: in3dId,
      img_upload_url: imgUploadUrl,
    } = resp.data;
    this.scannerId = id;
    this.tid = tid;
    console.log(`[SCAN][INITIAL] Done. TID: ${tid}`);
    if (localStorage) {
      localStorage.setItem(LAST_TID_STORAGE_KEY, tid);
    }
    this.scanVer = scanVerResp;
    this.protocolVer = protocolVer;
    // for collect tg3d mobile scan, use imgUploadUrl to upload front & side photo
    if (provider === 10) {
      this.imgUploadUrl = imgUploadUrl || '';
      return;
    }
    // for mobile scan, we only need the in3d id
    if (type === 'mobile') {
      this.in3dId = in3dId || '';
      return;
    }
    if (localStorage) {
      localStorage.setItem(SCANNERID_STORAGE_KEY, id);
      localStorage.setItem(TID_STORAGE_KEY, tid);
    }
    await this.confirm();
  },
  async reInitialize(sessionKey) {
    let key = sessionKey;
    if (!key) {
      key = await this.getSessionKey();
    }
    await this.initial({ id: this.scannerId, key, scanVer: this.scanVer });
  },
  async confirm() {
    this.reportEvent('confirm');
    if (ENVS.mock) return wait(1);

    return $api.user.post(`/api/v1/scanners/${this.scannerId}/${this.tid}/confirm`, null, {
      timeout: 5000,
    });
  },
  async start(isStep2) {
    this.reportEvent(isStep2 ? 'start_s2' : 'start');
    if (ENVS.mock) return wait(1);

    return $api.user.post(
      `/api/v1/scanners/${this.scannerId}/${this.tid}/${isStep2 ? 'start_s2' : 'start'}`,
      null,
      { timeout: 5000 }
    );
  },
  async cancel() {
    if (ENVS.mock) {
      await wait(2);
      this.resetState();
      return;
    }

    if (this.scannerId && this.tid) {
      let hasResponse = true;
      try {
        console.log('[SCAN][CANCEL] Canceling: ', this.scannerId, this.tid);
        await $api.user.post(`/api/v1/scanners/${this.scannerId}/${this.tid}/cancel`);
        await wait(3);
      } catch (error) {
        console.log('[SCAN][CANCEL] Error canceling scanner: ', error.message);
        hasResponse = error.response != null;
      } finally {
        if (hasResponse) this.resetState();
      }
    } else {
      this.resetState();
    }
  },
  async waitScan(isStep2) {
    this.reportEvent(isStep2 ? 'wait_scan_s2' : 'wait_scan');
    if (ENVS.mock) return wait(5);

    return $api.user.post(
      `/api/v1/scanners/${this.scannerId}/${this.tid}/${isStep2 ? 'wait_scan_s2' : 'wait_scan'}`,
      null,
      { timeout: 5000 }
    );
  },
  async waitFinish() {
    this.reportEvent('wait_finish');
    if (ENVS.mock) {
      await wait(40);
      return { url: this.isFootScanner() ? '/feet.tgs' : '/tg3d.tgs' };
    }

    const config = {};
    if (this.provider === 10) {
      config.timeout = 60000;
    }
    // FIXME: this might go wrong in the scan-again scenario
    const resp = await $api.user.post(
      `/api/v1/scanners/${this.scannerId}/${this.tid}/wait_finish`,
      null,
      config
    );
    console.log('[SCAN] waitFinish succeeded');
    return resp.data;
  },
  async waitSnapshot() {
    const resp = await $api.user.post(`/api/v1/scanners/${this.scannerId}/${this.tid}/wait_snaps`);
    const snapshots = resp.data;
    console.log(snapshots);
    return snapshots;
  },
  async waitMeasurement(tid) {
    if (ENVS.mock) {
      await wait(5);
      const url = this.isFootScanner() ? '/feet-sizext.json' : '/measurements.json';
      const resp = await axios.get(url);
      return resp.data;
    }

    const promises = [
      $api.user.post(`/api/v1/scanners/${this.scannerId}/${tid}/wait_measurement`),
      $api.user
        .get(`/api/v1/scan_records/${tid}/customized_measurements`)
        .catch(() => ({ data: [] })),
    ];
    if (this.twoStepScan() && this.waitIPose()) {
      promises.push(
        $api.user
          .post(`/api/v1/scanners/${this.scannerId}/${tid}/wait_measurement?pose=I`)
          .catch(() => ({}))
      );
    }
    const results = await Promise.all(promises);
    const [{ data }, { data: customizedMeasurements }] = results;
    data.size_xt_custom_name = customizedMeasurements;

    // get data from I pose
    if (results.length > 2 && results[2].data) {
      data.size_xt_pose_i = results[2].data;
    }

    console.log('[SCAN] waitMeasurement succeeded');
    return data;
  },
  async waitMeasurementSnapshot() {
    const resp = await $api.user.post(
      `/api/v1/scanners/${this.scannerId}/${this.tid}/wait_measurement_snaps`
    );
    const measurementSnapshots = resp.data;
    console.log(measurementSnapshots);
    return measurementSnapshots;
  },
  checksumAsync(blob) {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(blob);
      reader.onload = event => {
        resolve(MD5(event.target.result));
      };
    });
  },
  async finishMobileScan() {
    console.log('[SCAN] start finishMobileScan');
    if (ENVS.mock) {
      await wait(2);
      return;
    }

    if (this.mobileScanResult === null) {
      console.log('[SCAN] data is not defined');
      throw new Error(1001);
    }

    const { frontImage, sideImage, frontLandmarks, sideLandmarks } = this.mobileScanResult;
    if (!frontImage || !sideImage || !frontLandmarks || !sideLandmarks) {
      console.log('[SCAN] data is missing');
      throw new Error(1002);
    }

    const zip = new JSZip();
    try {
      let md5;
      const height = Math.round(this.mobileScanHeight * 10);

      // everything up to and including the S3 upload: no API errno to salvage, so 1003 stands
      // for "the photos never reached the backend"
      try {
        const frontImageBlob = (await fetch(frontImage)).blob();
        const sideImageBlob = (await fetch(sideImage)).blob();
        zip.file('front.jpg', frontImageBlob);
        zip.file('side.jpg', sideImageBlob);
        zip.file('front.json', frontLandmarks);
        zip.file('side.json', sideLandmarks);
        zip.file('height.txt', this.mobileScanHeight.toString());

        const file = await zip.generateAsync({ type: 'blob' });
        md5 = await this.checksumAsync(file);
        await this.uploadMobileScan(file);
      } catch (error) {
        console.log('[ERROR] finishMobileScan error: ', error);
        throw new Error(1003);
      }

      // /scanned is the only step that tells us *why* the photos were rejected, so its errno is
      // carried out to the caller -- see modules/scan-quality-error for how it is read back.
      try {
        await this.finishScanned({ height, md5 });
      } catch (error) {
        const errno = op.get(error, 'response.data.error.errno', 0);
        console.log(`[ERROR] finishScanned rejected: ${errno}`, error);
        const rejection = new Error(String(errno || 1003));
        rejection.errno = errno;
        throw rejection;
      }

      console.log('[SCAN] finishMobileScan succeeded');
    } finally {
      this.mobileScanResult = null;
    }
  },
  async uploadMobileScan(zipFile) {
    if (ENVS.mock) return wait(1);

    if (this.imgUploadUrl === null) throw new Error('imgUploadUrl is not defined.');
    return axios.put(this.imgUploadUrl, zipFile, { timeout: 30000 });
  },
  // only for tg3d mobile scan after upload images
  async finishScanned(data) {
    if (ENVS.mock) return wait(1);

    return $api.user.post(`/api/v1/scanners/${this.scannerId}/${this.tid}/scanned`, data, {
      headers: { 'X-Scanner-Token': this.token, 'Content-Type': 'application/json' },
    });
  },
  async release() {
    if (ENVS.mock) {
      this.tid = '';
      if (localStorage) {
        localStorage.removeItem(TID_STORAGE_KEY);
      }
      await wait(2);
      return;
    }

    // mobile scan not support release api
    if (this.provider === 10) {
      this.tid = '';
      if (localStorage) {
        localStorage.removeItem(TID_STORAGE_KEY);
      }
    }

    if (this.scannerId && this.tid) {
      try {
        await $api.user.post(`/api/v1/scanners/${this.scannerId}/${this.tid}/release`);
        console.log(`[SCAN][RELEASE] Done. TID: ${this.tid}`);
        this.tid = '';
        if (localStorage) {
          localStorage.removeItem(TID_STORAGE_KEY);
        }
      } catch (error) {
        console.log('[SCAN][RELEASE] Error releasing scanner', error.message);
      }
    }
  },
  async forceRelease(id) {
    const key = await this.getSessionKey(id);
    await $api.user.post(`/api/v1/scanners/${id}/force_release`, { session_key: key });
    console.log(`[SCAN][FORCE RELEASE] Done.`);
  },
  twoStepScan() {
    if (ENVS.mock) return [1, 3].indexOf(ENVS.mock) > -1;

    return this.scanVer === 2;
  },
  withHandles() {
    if (ENVS.mock) return [3, 4].indexOf(ENVS.mock) > -1;

    return parseInt(this.protocolVer, 10) < 4;
  },
  isOngoing() {
    return !!this.tid;
  },
  isFootScanner() {
    return this.type === 'foot';
  },
  isMobile() {
    return this.type === 'mobile';
  },
  waitIPose() {
    let hasIPose = false;
    try {
      const scannersConfigs = Storage.getScannersConfigs();
      hasIPose =
        scannersConfigs &&
        scannersConfigs[this.scannerId] &&
        scannersConfigs[this.scannerId].custom_measurements &&
        Array.isArray(scannersConfigs[this.scannerId].custom_measurements) &&
        scannersConfigs[this.scannerId].custom_measurements.some(m => m.pose === 'I');
    } catch (error) {
      console.log('[ERROR] checking ipose error: ', error);
      hasIPose = false;
    }
    return hasIPose;
  },
  reportEvent(action) {
    try {
      const { scannerId, tid, type } = this;
      const prefix = this.isMobile() ? 'mobile_scan' : 'scan';
      Vue.$gtag.event(`${prefix}/${action}`, {
        event_category: 'scan_process',
        non_interaction: true,
        scanner_id: scannerId || 'null',
        scanner_type: type || 'null',
        tid: tid || 'null',
      });
    } catch (error) {
      console.log('[ERROR] reportEvent error: ', error);
    }
  },
};

export default ScanProcess;
